import {IssueClosedIcon, IssueOpenedIcon, SkipIcon} from '@primer/octicons-react'
import {Link, Token} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import type {FC} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {IssueStateReason, TrackedByFragment$key} from './__generated__/TrackedByFragment.graphql'
import styles from './TrackedBy.module.css'

type TrackingIssue = {
  number: number
  url: string
  stateReason?: IssueStateReason | null
}

type TrackedByProps = {
  trackedByKey?: TrackedByFragment$key
  url: string
  isSmall?: boolean
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const TrackedByFragment = graphql`
  fragment TrackedByFragment on Issue {
    ... on Issue {
      trackedInIssues(first: 10) {
        nodes {
          number
          url
          stateReason
        }
        totalCount
      }
    }
  }
`

export const SmallTrackedBy: FC<TrackedByProps> = props => <TrackedBy {...props} isSmall />

export function TrackedBy({trackedByKey, url, isSmall = false}: TrackedByProps) {
  const data = useFragment(TrackedByFragment, trackedByKey)

  if (!data) {
    return null
  }

  const numberOfTrackingIssues = data.trackedInIssues?.totalCount ?? 0
  if (numberOfTrackingIssues === 0) return null

  const trackingIssues = data.trackedInIssues?.nodes?.flatMap(a => (a ? a : [])) ?? []

  const trackingItem =
    trackingIssues.length === 1 ? (
      <SingleTrackingItem trackingIssue={trackingIssues[0]!} url={url} omitIcon={isSmall} omitHover={isSmall} />
    ) : (
      <MultipleTrackingItems trackingIssues={trackingIssues} url={url} omitHover={isSmall} />
    )

  const tokenContent = <div className={styles.trackingItemContainer}>{trackingItem}</div>

  if (isSmall) {
    return tokenContent
  }

  return (
    <div>
      <Token text={tokenContent} size={'xlarge'} className={styles.trackingToken} />
    </div>
  )
}

function getIssueIcon(stateReason?: IssueStateReason | null) {
  if (stateReason === 'COMPLETED') {
    return {
      colorClass: 'iconDone' as const,
      icon: IssueClosedIcon,
    }
  } else if (stateReason === 'NOT_PLANNED') {
    return {
      colorClass: 'iconMuted' as const,
      icon: SkipIcon,
    }
  } else {
    return {
      colorClass: 'iconOpen' as const,
      icon: IssueOpenedIcon,
    }
  }
}

type TrackingItemBaseProps = {
  url: string
  omitIcon?: boolean
  omitHover?: boolean
}
type SingleTrackingItemProps = {
  trackingIssue: TrackingIssue
} & TrackingItemBaseProps

const SingleTrackingItem: FC<SingleTrackingItemProps> = ({trackingIssue, url, omitIcon, omitHover}) => {
  const icon = getIssueIcon(trackingIssue.stateReason)

  return (
    <>
      <span
        data-hovercard-url={!omitHover ? `${url}/tracked_in/hovercard/` : undefined}
        className={styles.trackedByText}
      >
        Listed in
      </span>
      <div
        key={trackingIssue.number}
        data-hovercard-url={!omitHover ? `${trackingIssue.url}/hovercard` : undefined}
        className={styles.trackingInfoContainer}
      >
        {!omitIcon && <Octicon icon={icon.icon} size={16} className={styles[icon.colorClass]} />}
        <Link href={trackingIssue.url}>{`#${trackingIssue.number}`}</Link>
      </div>
    </>
  )
}

type MultipleTrackingItemProps = {
  trackingIssues: TrackingIssue[]
} & TrackingItemBaseProps

const MultipleTrackingItems: FC<MultipleTrackingItemProps> = ({trackingIssues, url, omitHover}) => (
  <span
    data-hovercard-url={!omitHover ? `${url}/tracked_in/hovercard/` : undefined}
  >{`Listed in ${trackingIssues.length} issues`}</span>
)

try{ TrackedBy.displayName ||= 'TrackedBy' } catch {}
try{ SingleTrackingItem.displayName ||= 'SingleTrackingItem' } catch {}
try{ MultipleTrackingItems.displayName ||= 'MultipleTrackingItems' } catch {}